import { Drawer } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import { useCallback, useMemo, useRef } from 'react';
import { darkModeRoot } from 'src/app/App.css';
import { MobileHeaderBar } from 'src/components/mobile/MobileHeader';
import { vars } from 'src/core/themes';
import { globalMobileWrapper } from 'src/core/themes/themeContract.css';
import { Stack } from 'src/core/ui';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { SolidMoreIcon } from 'src/svgs';
import { ListingGroupStatusSummary } from 'src/tables/ListingTable/configs/ListingGroupStatusSummary';
import { BulkActionsDrawer } from 'src/tiles/BulkActionsContent';
import { useInventoryListingBulkActions } from 'src/tiles/ListingTilesV2/ListingTilesBulkActions.hooks';
import { ActionOutboxEntityType, Event } from 'src/WebApiController';

import { useCatalogDataContext } from '../CatalogDataContext';
import { useMultiSelectionContext } from '../MultiSelectionContext';
import { useSiteTheme } from '../SiteTheme/SiteThemeContext';
import * as drawerStyles from './MobileListingDrawer.css';
import {
  useMobileListingDrawerContext,
  ViewMode,
} from './MobileListingDrawerContext';
import { MobileListingDrawerZIndex } from './MobileListingDrawerContext.constants';
import { MobileListingGroupDetailsView } from './views/MobileListingGroupDetailsView';
import { MobileListingGroupSideView } from './views/MobileListingGroupSideView';

const ScrollToTopThreshold = 120;

export const MobileListingGroupDrawer = ({
  event,
}: {
  event?: Event | null;
}) => {
  const { isDarkMode } = useSiteTheme();
  const { viewMode, onNavViewMode, selectedListingGroup } =
    useMobileListingDrawerContext();
  const { itemSelection, setSelectionMode, setGroupItems } =
    useMultiSelectionContext();
  const [showBulkActions, { toggle: toggleBulkActions }] = useDisclosure();

  const contentRef = useRef<HTMLDivElement>(null);
  const onHeaderClick = useCallback(() => {
    if (
      contentRef.current &&
      contentRef.current.scrollTop > ScrollToTopThreshold
    ) {
      contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const { eventsTransformed } = useCatalogDataContext();
  const eventWithData = useMemo(
    () => eventsTransformed?.find((e) => e.event.virtId === event?.virtId),
    [event?.virtId, eventsTransformed]
  );

  const onBulkActionsClick = useCallback(() => {
    if (!event || !selectedListingGroup) {
      return;
    }
    const groupListingSelections = flattenListingGroup(
      selectedListingGroup
    ).reduce(
      (acc, listing) => {
        acc[listing.id] = true;
        return acc;
      },
      {} as Record<string, boolean>
    );
    setGroupItems(event.virtId, groupListingSelections);
    toggleBulkActions();
  }, [event, selectedListingGroup, setGroupItems, toggleBulkActions]);
  const listingActionGroups = useInventoryListingBulkActions();

  if (!selectedListingGroup || !event) {
    return;
  }

  return (
    <>
      <Drawer.Root
        id={'MobileListingGroupDrawer'}
        opened={!!selectedListingGroup}
        onClose={() => onNavViewMode({ back: true })}
        size="100%"
        position="right"
        className={clsx(globalMobileWrapper, {
          [darkModeRoot]: isDarkMode,
        })}
        zIndex={MobileListingDrawerZIndex.ListingGroupDetails}
      >
        <Drawer.Content ref={contentRef} className={drawerStyles.drawerContent}>
          <Drawer.Header
            className={drawerStyles.drawerHeader}
            onClick={onHeaderClick}
          >
            <MobileHeaderBar
              onBack={() => onNavViewMode({ back: true })}
              title={<span>{selectedListingGroup.groupName}</span>}
              actions={
                <Stack direction="row" gap="m" alignItems="center">
                  <ListingGroupStatusSummary
                    listingGroup={selectedListingGroup}
                  />
                  {viewMode === ViewMode.Details && (
                    <SolidMoreIcon
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onNavViewMode({ viewMode: ViewMode.SideView });
                      }}
                      size={vars.iconSize.s}
                      fill={vars.color.textSecondary}
                    />
                  )}
                </Stack>
              }
            />
          </Drawer.Header>
          <Drawer.Body
            className={drawerStyles.drawerBody}
            style={{
              backgroundColor:
                viewMode === ViewMode.Details
                  ? vars.color.backgroundPrimary
                  : vars.color.backgroundSecondary,
            }}
          >
            {viewMode === ViewMode.Details && (
              <MobileListingGroupDetailsView
                event={event}
                onBulkActionsClick={onBulkActionsClick}
              />
            )}
            {viewMode === ViewMode.SideView && (
              <MobileListingGroupSideView
                event={event}
                listingGroup={selectedListingGroup}
              />
            )}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Root>

      <BulkActionsDrawer
        event={eventWithData}
        entityType={ActionOutboxEntityType.Listing}
        actionGroups={listingActionGroups}
        onClose={() => {
          setSelectionMode(undefined);
          toggleBulkActions();
        }}
        show={!isEmpty(itemSelection) && showBulkActions}
      />
    </>
  );
};
